import { InputWrapper } from '@components/InputWrapper'
import { FormField } from '@components/ui/form.tsx'
import { useEffect, useRef } from 'react'
import {
  type ControllerFieldState,
  type ControllerRenderProps,
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
  useFormContext,
} from 'react-hook-form'
import { ComboboxBase, type ComboboxItem } from './ComboboxBase.tsx'

/**
 * Common props shared between single and multiple combobox form fields.
 *
 * @template T - The type of values in the combobox (string or number)
 * @template F - The form values type
 */
interface ComboboxFormFieldCommonProps<T extends string | number, F extends FieldValues> {
  /** The form field name/path */
  name: FieldPath<F>
  /** Label displayed above the combobox */
  label: string
  /** Optional description text shown below the label */
  description?: string
  /** Optional help text shown as a tooltip or helper text */
  helpText?: string
  /** Array of items to display in the combobox */
  items: ComboboxItem<T>[]
  /** Placeholder text when no value is selected */
  placeholder?: string
  /** Placeholder text for the search input */
  searchPlaceholder?: string
  /** Text shown when no search results are found */
  noResultsText?: string
  /** Whether the combobox is disabled */
  disabled?: boolean
  /** Additional CSS class name */
  className?: string
  /** Whether items should display on two lines (label + description) */
  twoLineItems?: boolean
  required?: boolean
  onSearchChange?: (search: string) => void
  onOpenChange?: (open: boolean) => void
  isLoading?: boolean
  isError?: boolean
  loadingText?: string
  errorText?: string
  selectedItem?: ComboboxItem<T>
  searchLabel?: string
}

type ComboboxPassThroughProps<T extends string | number> = Pick<
  ComboboxFormFieldCommonProps<T, FieldValues>,
  | 'required'
  | 'onSearchChange'
  | 'onOpenChange'
  | 'isLoading'
  | 'isError'
  | 'loadingText'
  | 'errorText'
  | 'selectedItem'
  | 'searchLabel'
>

/**
 * Props for a single-select combobox form field.
 *
 * @example
 * ```tsx
 * <ComboboxFormField
 *   name="category"
 *   label="Category"
 *   items={categories}
 *   defaultValue="default"
 * />
 * ```
 */
interface ComboboxFormFieldPropsSingle<T extends string | number, F extends FieldValues>
  extends ComboboxFormFieldCommonProps<T, F> {
  /** Single-select mode (default) */
  multiple?: false
  /** Default value for single-select mode */
  defaultValue?: T
  onValueChange?: (value: T | null) => void
}

/**
 * Props for a multi-select combobox form field.
 *
 * @example
 * ```tsx
 * <ComboboxFormField
 *   name="tags"
 *   label="Tags"
 *   items={tags}
 *   multiple
 *   defaultValue={["tag1", "tag2"]}
 * />
 * ```
 */
interface ComboboxFormFieldPropsMultiple<T extends string | number, F extends FieldValues>
  extends ComboboxFormFieldCommonProps<T, F> {
  /** Multi-select mode */
  multiple: true
  /** Default values for multi-select mode */
  defaultValue?: T[]
}

/**
 * Union type for combobox form field props, discriminated by the `multiple` property.
 */
type ComboboxFormFieldProps<T extends string | number, F extends FieldValues> =
  | ComboboxFormFieldPropsSingle<T, F>
  | ComboboxFormFieldPropsMultiple<T, F>

/**
 * Props for rendering a single-select combobox.
 *
 * @template T - The type of values in the combobox
 * @template F - The form values type
 */
interface RenderSingleComboboxProps<T extends string | number, F extends FieldValues> {
  field: ControllerRenderProps<F, FieldPath<F>>
  fieldState: ControllerFieldState
  items: ComboboxItem<T>[]
  placeholder?: string
  searchPlaceholder?: string
  noResultsText?: string
  disabled?: boolean
  twoLineItems: boolean
  label: string
  description?: string
  helpText?: string
  passThroughProps: ComboboxPassThroughProps<T>
  onValueChange?: (value: T | null) => void
}

/**
 * Props for rendering a multi-select combobox.
 *
 * @template T - The type of values in the combobox
 * @template F - The form values type
 */
interface RenderMultipleComboboxProps<T extends string | number, F extends FieldValues> {
  field: ControllerRenderProps<F, FieldPath<F>>
  fieldState: ControllerFieldState
  items: ComboboxItem<T>[]
  placeholder?: string
  searchPlaceholder?: string
  noResultsText?: string
  disabled?: boolean
  twoLineItems: boolean
  label: string
  description?: string
  helpText?: string
  passThroughProps: ComboboxPassThroughProps<T>
}

/**
 * Type guard to check if a value is an array (for multi-select).
 */
const isArrayValue = (value: unknown): value is unknown[] => {
  return Array.isArray(value)
}

/**
 * Normalizes a form field value for single-select mode.
 * Converts empty strings and null to null, otherwise returns the value as T | null.
 */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
const normalizeSingleValue = <T extends string | number>(value: unknown): T | null => {
  if (value === undefined || value === '' || value === null) {
    return null
  }
  return value as T
}

/**
 * Normalizes a form field value for multi-select mode.
 * Ensures the value is always an array of T.
 */
const normalizeMultipleValue = <T extends string | number>(value: unknown): T[] => {
  if (!isArrayValue(value)) {
    return []
  }
  return value as T[]
}

/**
 * Renders a single-select combobox form field.
 *
 * @template T - The type of values in the combobox
 * @template F - The form values type
 */
const renderSingleCombobox = <T extends string | number, F extends FieldValues>({
  field,
  fieldState,
  items,
  placeholder,
  searchPlaceholder,
  noResultsText,
  disabled,
  twoLineItems,
  label,
  description,
  helpText,
  passThroughProps,
  onValueChange,
}: RenderSingleComboboxProps<T, F>) => {
  const value = normalizeSingleValue<T>(field.value)

  return (
    <InputWrapper name={field.name} label={label} description={description} helpText={helpText}>
      <ComboboxBase<T>
        {...passThroughProps}
        items={items}
        value={value}
        onSelect={(nextValue) => {
          field.onChange(nextValue)
          onValueChange?.(nextValue)
        }}
        onBlur={field.onBlur}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        noResultsText={noResultsText}
        disabled={disabled}
        multiple={false}
        hasError={!!fieldState.error}
        twoLineItems={twoLineItems}
      />
    </InputWrapper>
  )
}

/**
 * Renders a multi-select combobox form field.
 *
 * @template T - The type of values in the combobox
 * @template F - The form values type
 */
const renderMultipleCombobox = <T extends string | number, F extends FieldValues>({
  field,
  fieldState,
  items,
  placeholder,
  searchPlaceholder,
  noResultsText,
  disabled,
  twoLineItems,
  label,
  description,
  helpText,
  passThroughProps,
}: RenderMultipleComboboxProps<T, F>) => {
  const value = normalizeMultipleValue<T>(field.value)

  return (
    <InputWrapper name={field.name} label={label} description={description} helpText={helpText}>
      <ComboboxBase<T>
        {...passThroughProps}
        items={items}
        value={value}
        onSelect={field.onChange}
        onBlur={field.onBlur}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        noResultsText={noResultsText}
        disabled={disabled}
        multiple
        hasError={!!fieldState.error}
        twoLineItems={twoLineItems}
      />
    </InputWrapper>
  )
}

/**
 * Type guard to check if a value is considered "empty" for single-select mode.
 */
const isEmptySingleValue = (value: unknown): boolean => {
  return value === undefined || value === '' || value === null
}

/**
 * Type guard to check if a value is considered "empty" for multi-select mode.
 */
const isEmptyMultipleValue = (value: unknown): boolean => {
  return !isArrayValue(value) || value.length === 0
}

/**
 * A form field component that integrates ComboboxBase with React Hook Form.
 * Supports both single-select and multi-select modes via discriminated union props.
 *
 * @template T - The type of values in the combobox (string or number)
 * @template F - The form values type
 *
 * @example Single-select usage:
 * ```tsx
 * <ComboboxFormField
 *   name="category"
 *   label="Category"
 *   items={[
 *     { value: "tech", label: "Technology" },
 *     { value: "design", label: "Design" }
 *   ]}
 *   defaultValue="tech"
 * />
 * ```
 *
 * @example Multi-select usage:
 * ```tsx
 * <ComboboxFormField
 *   name="tags"
 *   label="Tags"
 *   multiple
 *   items={[
 *     { value: "react", label: "React" },
 *     { value: "typescript", label: "TypeScript" }
 *   ]}
 *   defaultValue={["react"]}
 * />
 * ```
 */
export const ComboboxFormField = <T extends string | number, F extends FieldValues>(
  props: ComboboxFormFieldProps<T, F>,
) => {
  const {
    name,
    label,
    description,
    helpText,
    items,
    placeholder,
    searchPlaceholder,
    noResultsText,
    disabled,
    className,
    twoLineItems = false,
    defaultValue,
    required,
    onSearchChange,
    onOpenChange,
    isLoading,
    isError,
    loadingText,
    errorText,
    selectedItem,
    searchLabel,
  } = props
  const passThroughProps: ComboboxPassThroughProps<T> = {
    required,
    onSearchChange,
    onOpenChange,
    isLoading,
    isError,
    loadingText,
    errorText,
    selectedItem,
    searchLabel,
  }
  const onValueChange = props.multiple === true ? undefined : props.onValueChange

  const isMultiple = props.multiple === true
  const form = useFormContext<F>()
  const hasInitializedDefault = useRef(false)

  // Handle default values for edge cases where RHF's defaultValue doesn't apply
  // (e.g., when field value is '' or null instead of undefined)
  useEffect(() => {
    if (defaultValue === undefined || hasInitializedDefault.current) {
      return
    }

    const currentValue = form.getValues(name)
    const isEmpty = isMultiple
      ? isEmptyMultipleValue(currentValue)
      : isEmptySingleValue(currentValue)

    if (isEmpty) {
      form.setValue(name, defaultValue as FieldPathValue<F, FieldPath<F>>)
    }

    hasInitializedDefault.current = true
  }, [defaultValue, form, isMultiple, name])

  return (
    <div className={className}>
      <FormField<F, FieldPath<F>>
        control={form.control}
        name={name}
        defaultValue={defaultValue as FieldPathValue<F, FieldPath<F>> | undefined}
        render={({ field, fieldState }) => {
          if (isMultiple) {
            return renderMultipleCombobox<T, F>({
              field,
              fieldState,
              items,
              placeholder,
              searchPlaceholder,
              noResultsText,
              disabled,
              twoLineItems,
              label,
              description,
              helpText,
              passThroughProps,
            })
          }

          return renderSingleCombobox<T, F>({
            field,
            fieldState,
            items,
            placeholder,
            searchPlaceholder,
            noResultsText,
            disabled,
            twoLineItems,
            label,
            description,
            helpText,
            passThroughProps,
            onValueChange,
          })
        }}
      />
    </div>
  )
}
